import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'

interface Patient {
  id: string
  patient_id: string
  full_name: string
  phone: string
}

interface Report {
  id: string
  report_id: string
  patient: string
  patient_name: string
  status: string
  created_at: string
  finalized_at?: string
  sent_to_his_lis?: boolean
  exported_pdf?: boolean
  report_type: 'histopathology' | 'cytology'
}

interface PatientWithReport {
  patient: Patient
  report: Report
  is_finalized: boolean
  is_delivered: boolean
}

const ResultsTab: React.FC = () => {
  const [patientsWithReports, setPatientsWithReports] = useState<PatientWithReport[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'finalized' | 'not_finalized'>('all')

  useEffect(() => {
    fetchPatientsWithReports()
  }, [])

  const fetchPatientsWithReports = async () => {
    setLoading(true)
    try {
      const [patientsRes, histoReportsRes, cytoReportsRes] = await Promise.all([
        fetch('/api/v1/pathology/patients/?page_size=1000'),
        fetch('/api/v1/pathology/reports/?page_size=1000'),
        fetch('/api/v1/pathology/cytology-reports/?page_size=1000')
      ])

      if (!patientsRes.ok) {
        throw new Error('Failed to fetch data')
      }

      const [patientsData, histoReportsData, cytoReportsData] = await Promise.all([
        patientsRes.json(),
        histoReportsRes.ok ? histoReportsRes.json() : Promise.resolve({ results: [] }),
        cytoReportsRes.ok ? cytoReportsRes.json() : Promise.resolve({ results: [] })
      ])

      const allPatients = patientsData.results || patientsData

      // Combine both types of reports
      const histoReports = (histoReportsData.results || histoReportsData).map((r: any) => ({
        ...r,
        report_type: 'histopathology'
      }))

      const cytoReports = (cytoReportsData.results || cytoReportsData).map((r: any) => ({
        id: r.id.toString(),
        report_id: `CYT-${r.id}`,
        patient: r.patient.toString(),
        patient_name: r.patient_name,
        status: r.status === 'draft' ? 'DRAFT' : r.status === 'finalized' ? 'FINALIZED' : 'PENDING_REVIEW',
        created_at: r.report_date || r.created_at,
        finalized_at: r.status === 'finalized' ? r.updated_at : undefined,
        sent_to_his_lis: false,
        exported_pdf: !!r.delivered_at,
        report_type: 'cytology'
      }))

      const allReports = [...histoReports, ...cytoReports]

      // Map reports by patient
      const reportsByPatient = new Map<string, Report>()
      allReports.forEach((report: Report) => {
        const patientId = report.patient.toString()
        // Keep latest report per patient
        const existing = reportsByPatient.get(patientId)
        if (!existing || new Date(report.created_at) > new Date(existing.created_at)) {
          reportsByPatient.set(patientId, report)
        }
      })

      // Build patient-report pairs (exclude delivered)
      const patientsWithReports: PatientWithReport[] = []

      allPatients.forEach((patient: Patient) => {
        const report = reportsByPatient.get(patient.id.toString())
        if (report) {
          const isDelivered = report.sent_to_his_lis || report.exported_pdf
          // Only include if not delivered yet
          if (!isDelivered) {
            patientsWithReports.push({
              patient,
              report,
              is_finalized: !!report.finalized_at,
              is_delivered: false
            })
          }
        }
      })

      // Sort by report creation date
      patientsWithReports.sort((a, b) =>
        new Date(b.report.created_at).getTime() - new Date(a.report.created_at).getTime()
      )

      setPatientsWithReports(patientsWithReports)
    } catch (error) {
      console.error('Error fetching results data:', error)
      toast.error('Không thể tải dữ liệu phiếu kết quả')
    } finally {
      setLoading(false)
    }
  }

  const filteredPatients = patientsWithReports
    .filter(pw => {
      const matchesSearch =
        pw.patient.patient_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pw.patient.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pw.patient.phone.includes(searchTerm)

      const matchesStatus =
        filterStatus === 'all' ||
        (filterStatus === 'finalized' && pw.is_finalized) ||
        (filterStatus === 'not_finalized' && !pw.is_finalized)

      return matchesSearch && matchesStatus
    })

  const stats = {
    total: patientsWithReports.length,
    finalized: patientsWithReports.filter(pw => pw.is_finalized).length,
    not_finalized: patientsWithReports.filter(pw => !pw.is_finalized).length
  }

  return (
    <div>
      {/* Search and Filter */}
      <div className="mb-4 flex gap-4">
        <input
          type="text"
          placeholder="Tìm theo mã BN, họ tên, số điện thoại..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Tất cả ({stats.total})</option>
          <option value="not_finalized">Chưa hoàn thành ({stats.not_finalized})</option>
          <option value="finalized">Đã hoàn thành ({stats.finalized})</option>
        </select>
      </div>

      {/* Stats */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-4 text-sm">
          <span className="text-gray-600">
            Tổng số: <span className="font-semibold text-gray-900">{stats.total}</span>
          </span>
          <span className="text-purple-600">
            Chưa hoàn thành: <span className="font-semibold">{stats.not_finalized}</span>
          </span>
          <span className="text-teal-600">
            Đã hoàn thành: <span className="font-semibold">{stats.finalized}</span>
          </span>
        </div>
        <button
          onClick={fetchPatientsWithReports}
          className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100"
        >
          🔄 Làm mới
        </button>
      </div>

      {/* Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mã BN
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Họ và Tên
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mã phiếu
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Loại
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Trạng thái
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ngày tạo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Thao tác
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  Đang tải dữ liệu...
                </td>
              </tr>
            ) : filteredPatients.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  {searchTerm || filterStatus !== 'all' ? 'Không tìm thấy phiếu' : 'Tất cả phiếu đã trả kết quả 🎉'}
                </td>
              </tr>
            ) : (
              filteredPatients.map((pw) => (
                <tr key={pw.patient.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {pw.patient.patient_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {pw.patient.full_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {pw.report.report_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      pw.report.report_type === 'histopathology'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {pw.report.report_type === 'histopathology' ? 'Mô bệnh học' : 'Tế bào học'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      pw.is_finalized
                        ? 'bg-teal-100 text-teal-700'
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {pw.is_finalized ? '📄 Đã hoàn thành' : '📝 Chưa hoàn thành'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(pw.report.created_at).toLocaleDateString('vi-VN')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    <Link
                      to="/pathology/reports"
                      className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 font-medium"
                    >
                      👁️ Xem phiếu
                    </Link>
                    {pw.is_finalized ? (
                      <Link
                        to="/workflow/results"
                        className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
                      >
                        ✅ Trả kết quả
                      </Link>
                    ) : (
                      <Link
                        to="/pathology/reports"
                        className="inline-flex items-center px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 font-medium"
                      >
                        ✓ Hoàn thành
                      </Link>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default ResultsTab
